import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { Box, Stack, TextField, Typography, Button, Alert } from '@mui/material';
import { styled } from '@mui/system';
import { auth } from '../../../firebase';

// Styled components
const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
}));

const FormWrapper = styled(Stack)(({ theme }) => ({
  width: '100%',
  maxWidth: 400,
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[5],
  borderRadius: theme.shape.borderRadius,
}));

export default function Login() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const handleLogin = async (event) => {
    event.preventDefault();
    const email = event.target.email.value;
    const password = event.target.password.value;

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard/app');
    } catch (err) {
      setError('Login failed. Please check your email and password.');
    }
  };

  const handlePasswordReset = async (event) => {
    event.preventDefault();

    if (!resetEmail) {
      setError('Please enter your email address to reset your password.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Please check your email for instructions.');
      setError('');
    } catch (err) {
      setError('Failed to send password reset email. Please try again.');
      setResetMessage('');
    }
  };

  const handleToggleResetForm = () => {
    setShowResetForm((prev) => !prev);
    setError('');
    setResetMessage('');
  };

  const handleSignUp = () => {
    navigate('/register');
  };

  return (
    <LoginContainer>
      <Typography variant="h4" gutterBottom>
        Welcome to Ballarat Squash & Racquetball!
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
        Please {showResetForm ? 'reset your password' : 'login'} or register to continue.
      </Typography>

      {showResetForm ? (
        <FormWrapper component="form" onSubmit={handlePasswordReset} spacing={2}>
          {error && (
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          )}
          {resetMessage && (
            <Alert severity="success" variant="filled">
              {resetMessage}
            </Alert>
          )}
          <TextField
            name="resetEmail"
            label="Email Address"
            type="email"
            fullWidth
            required
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
          />
          <Button type="submit" variant="contained" fullWidth>
            Send Reset Email
          </Button>
          <Button variant="text" fullWidth onClick={handleToggleResetForm}>
            Back to Login
          </Button>
        </FormWrapper>
      ) : (
        <FormWrapper component="form" onSubmit={handleLogin} spacing={2}>
          {error && (
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          )}
          {resetMessage && (
            <Alert severity="success" variant="filled">
              {resetMessage}
            </Alert>
          )}
          <TextField name="email" label="Email Address" type="email" fullWidth required />
          <TextField name="password" label="Password" type="password" fullWidth required />
          <Button type="submit" variant="contained" fullWidth>
            Login
          </Button>
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
            Forgot your password?{' '}
            <Button variant="text" onClick={handleToggleResetForm}>
              Reset Password
            </Button>
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
            Don't have an account?{' '}
            <Button variant="text" onClick={handleSignUp}>
              Register Here
            </Button>
          </Typography>
        </FormWrapper>
      )}
    </LoginContainer>
  );
}
